import { AnalyticsProvider, useAnalytics } from './context/AnalyticsContext';
import KpiCards        from './components/KpiCards';
import TopModulesChart from './components/TopModulesChart';
import TopUsersChart   from './components/TopUsersChart';
import DailyChart      from './components/DailyChart';
import HourlyChart     from './components/HourlyChart';
import RecentLog       from './components/RecentLog';

// Fallback list — used when GetProjects returns empty (e.g. before backfill runs)
const FALLBACK_PROJECTS = [
	'VMO2 Dashboards',
	'VMO2 Ran',
	'VMO2 Radio Design',
	'3GSD',
];

const PERIOD_LABEL = {
	0: 'Today', 7: 'Last 7 days', 14: 'Last 14 days',
	21: 'Last 3 weeks', 30: 'Last 30 days', 90: 'Last 90 days',
};

function DashboardContent() {
	const {
		state, changeDays, changeTopN,
		selectRole, selectProject,
		loadDashboard, clearFilter, clearDate,
	} = useAnalytics();

	const {
		selectedModule, selectedUser, selectedRole, selectedProject,
		filterType, filterLoading, selectedDate,
		availableRoles, availableProjects,
	} = state;

	// Use API projects if available, otherwise fallback to hardcoded list
	const projectOptions = availableProjects.length > 0 ? availableProjects : FALLBACK_PROJECTS;

	const hasFilter    = !!filterType;
	const hasDrillDown = !!(selectedModule || selectedUser);

	const filterParts = [
		selectedRole    && `Role: ${selectedRole.label}`,
		selectedProject && `Project: ${selectedProject}`,
		selectedModule  && `Module: ${selectedModule.label}`,
		selectedUser    && `User: ${selectedUser.label}`,
	].filter(Boolean);

	const filterLabel = filterParts.join(' · ');

	const dateLabelFmt = selectedDate
		? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
		: '';

	return (
		<div style={S.root}>
			<style>{GLOBAL_CSS}</style>

			{/* TOP BAR */}
			<div style={S.topbar}>
				<div style={S.logoTitle}>Module Analytics</div>
				<div style={S.controls}>

					{/* Project filter — always visible */}
					<label style={S.ctrlLabel}>Project</label>
					<select
						style={{
							...S.select,
							borderColor: selectedProject ? '#2563EB' : '#E2E8F0',
							color:       selectedProject ? '#1D4ED8' : '#334155',
							fontWeight:  selectedProject ? 600 : 400,
						}}
						value={selectedProject ?? ''}
						onChange={e => selectProject(e.target.value || null)}
					>
						<option value=''>All projects</option>
						{projectOptions.map(p => (
							<option key={p} value={p}>{p}</option>
						))}
					</select>

					{/* Role filter — only if roles exist */}
					{availableRoles.length > 0 && (
						<>
							<label style={S.ctrlLabel}>Role</label>
							<select
								style={{
									...S.select,
									borderColor: selectedRole ? '#2563EB' : '#E2E8F0',
									color:       selectedRole ? '#1D4ED8' : '#334155',
									fontWeight:  selectedRole ? 600 : 400,
								}}
								value={selectedRole?.id ?? ''}
								onChange={e => {
									const val = e.target.value;
									if (!val) { selectRole(selectedRole ?? { id: '' }); return; }
									const role = availableRoles.find(r => r.id === val);
									if (role) selectRole(role);
								}}
							>
								<option value=''>All roles</option>
								{availableRoles.map(r => (
									<option key={r.id} value={r.id}>{r.label}</option>
								))}
							</select>
						</>
					)}

					<div style={S.divider} />

					{/* Top N */}
					<label style={S.ctrlLabel}>Top</label>
					<select style={S.select} value={state.topN} onChange={e => changeTopN(Number(e.target.value))}>
						<option value={5}>5</option>
						<option value={10}>10</option>
						<option value={20}>20</option>
						<option value={50}>50</option>
					</select>
					<div style={S.divider} />

					{/* Time range */}
					<select style={S.select} value={state.days} onChange={e => changeDays(Number(e.target.value))}>
						<option value={0}>Today</option>
						<option value={7}>Last 7 days</option>
						<option value={14}>Last 14 days</option>
						<option value={21}>Last 3 weeks</option>
						<option value={30}>Last 30 days</option>
						<option value={90}>Last 90 days</option>
					</select>

					<button style={S.btn} onClick={() => loadDashboard(state.days, state.topN)} disabled={state.loading}>
						<span style={{ marginRight: 6 }}>↻</span>
						{state.loading ? 'Loading…' : 'Refresh'}
					</button>
					{state.lastRefresh && (
						<span style={S.refreshTs}>Updated at {new Date(state.lastRefresh).toLocaleTimeString('en-GB')}</span>
					)}
				</div>
			</div>

			{/* ACTIVE FILTER BANNER */}
			{hasFilter && (
				<div style={S.filterBanner}>
					<div style={S.filterBannerLeft}>
						<span style={S.filterIcon}>🔍</span>
						<span>Filtering by</span>
						<strong style={{ color: '#1D4ED8' }}>{filterLabel}</strong>
						{filterLoading && <span style={S.filterSpinner}>· Loading…</span>}
					</div>
					<button style={S.clearBtn} onClick={clearFilter}>✕ Clear all filters</button>
				</div>
			)}

			{/* DATE FILTER BANNER */}
			{selectedDate && (
				<div style={{ ...S.filterBanner, background: '#F0FDF4', borderBottomColor: '#BBF7D0' }}>
					<div style={S.filterBannerLeft}>
						<span style={S.filterIcon}>📅</span>
						<span style={{ color: '#166534' }}>Hourly view for</span>
						<strong style={{ color: '#15803D' }}>{dateLabelFmt}</strong>
						{state.dateHourlyLoading && <span style={{ ...S.filterSpinner, color: '#16A34A' }}>· Loading…</span>}
					</div>
					<button style={{ ...S.clearBtn, borderColor: '#86EFAC', color: '#15803D' }} onClick={clearDate}>
						✕ Clear date
					</button>
				</div>
			)}

			{/* CONTENT */}
			<div style={S.body}>
				{state.error && <div style={S.error}><span style={{ marginRight: 8 }}>⚠</span>{state.error}</div>}

				<KpiCards />

				<div style={S.sectionLabel}>Module & User Overview</div>

				<div style={S.grid2}>
					<div style={S.card}>
						<div style={S.cardHeader}>
							<div style={S.cardTitle}>
								{selectedUser  ? `Modules accessed by "${selectedUser.label}"`
								: hasFilter    ? 'Most viewed modules (filtered)'
								: 'Most viewed modules'}
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								{!hasDrillDown && <span style={S.hintText}>Click to drill down ↓</span>}
								<div style={S.cardBadge}>
									{(hasFilter ? state.filteredModules?.length : state.topModules?.length) ?? 0} modules
								</div>
							</div>
						</div>
						<TopModulesChart />
					</div>
					<div style={S.card}>
						<div style={S.cardHeader}>
							<div style={S.cardTitle}>
								{selectedModule ? `Users who accessed "${selectedModule.label}"`
								: hasFilter     ? 'Most active users (filtered)'
								: 'Most active users'}
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
								{!hasDrillDown && <span style={S.hintText}>Click to drill down ↓</span>}
								<div style={S.cardBadge}>
									{(hasFilter ? state.filteredUsers?.length : state.topUsers?.length) ?? 0} users
								</div>
							</div>
						</div>
						<TopUsersChart />
					</div>
				</div>

				<div style={S.sectionLabel}>Timeline</div>

				<div style={{ ...S.card, marginBottom: 16 }}>
					<div style={S.cardHeader}>
						<div style={S.cardTitle}>
							{hasFilter ? `Daily access — ${filterLabel}` : 'Access per day'}
						</div>
						<div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
							{!selectedDate && <span style={S.hintText}>Click a bar to drill into hours ↓</span>}
							<div style={S.legendItem}><div style={{ ...S.legendDot, background: '#2563EB' }} />Total views</div>
							<div style={S.legendItem}><div style={{ ...S.legendDot, background: '#10B981' }} />Unique users</div>
						</div>
					</div>
					<DailyChart />
				</div>

				<div style={S.grid2}>
					<div style={S.card}>
						<div style={S.cardHeader}>
							<div style={S.cardTitle}>
								{selectedDate ? `Hourly breakdown — ${dateLabelFmt}` : 'Access by hour of day'}
							</div>
						</div>
						<HourlyChart />
					</div>
					<div style={S.card}>
						<div style={S.cardHeader}>
							<div style={S.cardTitle}>Recent access log</div>
							<div style={S.cardBadge}>
								{(selectedDate ? state.dateRecentLog?.length
								: hasFilter    ? state.filteredRecentLog?.length
								:               state.recentLog?.length) ?? 0} records
							</div>
						</div>
						<RecentLog />
					</div>
				</div>
			</div>
		</div>
	);
}

export default function AnalyticsDashboard() {
	return (
		<AnalyticsProvider>
			<DashboardContent />
		</AnalyticsProvider>
	);
}

const S = {
	root: { width: '100%', minHeight: '100vh', background: '#F4F6F9', color: '#1A2332', fontFamily: "'Outfit', 'Segoe UI', sans-serif" },
	topbar: {
		display: 'flex', alignItems: 'center', justifyContent: 'space-between',
		padding: '12px 24px', background: '#FFFFFF',
		borderBottom: '1px solid #E4E9F0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
		gap: 12, flexWrap: 'wrap',
	},
	logoTitle:  { fontSize: 15, fontWeight: 700, color: '#0F172A' },
	controls:   { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
	ctrlLabel:  { fontSize: 12, color: '#64748B', fontWeight: 600 },
	divider:    { width: 1, height: 20, background: '#E2E8F0' },
	select: {
		background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#334155',
		padding: '6px 12px', borderRadius: 8, fontSize: 13,
		cursor: 'pointer', fontFamily: 'inherit', outline: 'none',
	},
	btn: {
		background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
		border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 8,
		fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
		display: 'flex', alignItems: 'center',
		boxShadow: '0 1px 4px rgba(37,99,235,0.3)', transition: 'opacity .15s',
	},
	refreshTs: { fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap' },
	filterBanner: {
		display: 'flex', alignItems: 'center', justifyContent: 'space-between',
		padding: '10px 24px', background: '#EFF6FF',
		borderBottom: '1px solid #BFDBFE', flexWrap: 'wrap', gap: 8,
	},
	filterBannerLeft: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1E40AF', flexWrap: 'wrap' },
	filterIcon:    { fontSize: 16 },
	filterSpinner: { fontSize: 12, color: '#3B82F6' },
	clearBtn: {
		background: 'transparent', border: '1px solid #93C5FD', color: '#1D4ED8',
		padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
		cursor: 'pointer', fontFamily: 'inherit',
	},
	body: { padding: '20px 24px', width: '100%' },
	sectionLabel: {
		fontSize: 11, fontWeight: 700, color: '#94A3B8',
		letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, marginTop: 4,
	},
	grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16, marginBottom: 16 },
	card: { background: '#FFFFFF', border: '1px solid #E4E9F0', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.05)', minWidth: 0 },
	cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 },
	cardTitle:  { fontSize: 14, fontWeight: 700, color: '#0F172A' },
	cardBadge:  { fontSize: 11, fontWeight: 600, color: '#2563EB', background: '#EFF6FF', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' },
	hintText:   { fontSize: 11, color: '#94A3B8', fontStyle: 'italic' },
	legendItem: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748B' },
	legendDot:  { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
	error:      { background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '12px 18px', borderRadius: 10, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center' },
};

const GLOBAL_CSS = `
	@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
	* { box-sizing: border-box; }
	::-webkit-scrollbar { width: 5px; height: 5px; }
	::-webkit-scrollbar-track { background: #F1F5F9; }
	::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
	select:focus { outline: 2px solid #2563EB; outline-offset: 1px; }
	button:hover:not(:disabled) { opacity: 0.88; }
	button:disabled { opacity: 0.5; cursor: not-allowed; }
`;